import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import type {
    CancelTransactionRequest,
    ConsentV2Request,
    ContractPreliminaryRequest,
    ContractSignOrBuyRequest,
    CreatePinRequest,
    CreditPaymentPlanRequest,
    FinancialAccountNumberV2Request,
    FinancialProductV2Request,
    IbanDataRequest,
    JWTLoginRequest,
    OfferPlanDataRequest,
    PaymentDataRequest,
    QuestionnaireRequest,
    ReCreatePinRequest,
    TransactionRequest,
} from '@leanpay/models'
import type { StandardErrorResponse } from '@leanpay/services'
import { CheckoutStep, CheckoutStepViewType } from '@leanpay/models'
import { createStandardRetryHandler } from '@leanpay/query-hooks'
import {
    checkoutService,
    downloadService,
    getAuthToken,
    isStandardErrorResponse,
    logoutUser,
    saveBlobFile,
    storeTokenAsync,
} from '@leanpay/services'

import { useAppStore } from 'app/app-store'
import {
    ACCESS_DENIED_ERROR_STATE,
    CacheKey,
    CreditFailureReason,
    CreditRoute,
    UPSELL_RESPONSE_KEY_TO_VARIANT,
    UpsellBlockedVariant,
} from 'library/constants'
import { navigateToHandoffLanding } from 'library/utils'

// Credit deliberately keeps its own GPL/state-machine hooks; only the shared
// retry policy and currency lookup come from @leanpay/query-hooks.
export { useGetCurrency } from '@leanpay/query-hooks'

export const useValidateCreditToken = (token: string | null) => {
    return useQuery({
        queryKey: [CacheKey.CreditTokenValidation, token],
        queryFn: () => checkoutService.validateCreditToken(token!),
        enabled: !!token,
        retry: false,
        refetchOnWindowFocus: false,
    })
}

export const useGetSessionInfo = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        enabled: !!vendorToken && !!getAuthToken(),
        queryKey: [CacheKey.SessionInfo],
        queryFn: () => checkoutService.getSessionInfo(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useGetEligibilityValidationResult = () => {
    return useQuery({
        queryKey: [CacheKey.EligibilityValidation],
        queryFn: () => checkoutService.getEligibilityValidationResult(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useGetOfferValidationResult = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.OfferValidation, vendorToken],
        queryFn: () => checkoutService.getOfferValidationResult(vendorToken),
        enabled: !!vendorToken,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

const useStateMachineMutation = <TVariables>(
    mutationFn: (variables: TVariables) => Promise<unknown>,
) => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    return useMutation({
        mutationFn,
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [CacheKey.StateMachineStatus],
            })
        },
        onError: (error: Error) => {
            navigate(`/${CreditRoute.CreditError}`, {
                replace: true,
                state: { error: error.message },
            })
        },
    })
}

export const useSubmitConsents = () => {
    return useStateMachineMutation((request: ConsentV2Request) =>
        checkoutService.submitConsentsV2(request),
    )
}

export const useSubmitOfferPlanData = () => {
    const { vendorToken } = useAppStore()
    return useStateMachineMutation((request: OfferPlanDataRequest) =>
        checkoutService.submitOfferPlanData(vendorToken, request),
    )
}

export const useSubmitBankAccount = () => {
    return useStateMachineMutation((request: FinancialAccountNumberV2Request) =>
        checkoutService.checkBankAccountNumber(request),
    )
}

export const useGetIbanData = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.IbanData, vendorToken],
        queryFn: () => checkoutService.getIbanData(vendorToken),
        enabled: !!vendorToken && !!getAuthToken(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useSubmitIban = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (request: IbanDataRequest) => checkoutService.submitIbanData(request),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [CacheKey.StateMachineStatus],
            })
        },
    })
}

export const useSignContract = () => {
    return useStateMachineMutation(
        ({ payload, twoFaPin }: { payload: ContractSignOrBuyRequest; twoFaPin?: string }) =>
            checkoutService.signOrBuy(payload, twoFaPin),
    )
}

export const useConfirmFailure = () => {
    return useStateMachineMutation((token: string) =>
        checkoutService.confirmStateMachineFailure(token),
    )
}

export const useAuthenticate = () => {
    return useMutation({
        mutationFn: async (data: JWTLoginRequest) => {
            const res = await checkoutService.authenticate(data)
            await storeTokenAsync(res.idToken)
            return res
        },
    })
}

interface ExchangeHandoffVariables {
    handoffCode: string
    // Used by OTP login when the exchange fails.
    fallbackVendorToken: string | null
    isUpsell: boolean
}

// Routing must stay in these hook-level callbacks. Under StrictMode, mutate()-level callbacks never
// fire: the dev remount detaches them, and AppInit does not call mutate() a second time.
export const useExchangeHandoff = () => {
    const navigate = useNavigate()
    const { setVendorToken, setUpsellFlow, setIsAuthenticated } = useAppStore()

    return useMutation({
        mutationFn: async ({ handoffCode }: ExchangeHandoffVariables) => {
            const res = await checkoutService.exchangeHandoff(handoffCode)
            await storeTokenAsync(res.idToken)
            return res
        },
        onSuccess: (res, { isUpsell }) => {
            // Handoff skips the login screen, which records this step on every other entry.
            void checkoutService.recordStep({
                token: res.vendorToken,
                checkoutStep: CheckoutStep.LOGIN_AUTHENTIFICATION,
                checkoutStepViewType: CheckoutStepViewType.CLOSE,
                data: { authEntryMode: 'HANDOFF' },
            })
            setVendorToken(res.vendorToken)
            setUpsellFlow(isUpsell)
            // Must come before navigate: the landing route is behind ProtectedRoute.
            setIsAuthenticated(true)
            navigateToHandoffLanding(navigate)
        },
        onError: (_error, { fallbackVendorToken, isUpsell }) => {
            // Without a vendor token, OTP login cannot start.
            if (!fallbackVendorToken) {
                navigate(`/${CreditRoute.CreditError}`, {
                    state: ACCESS_DENIED_ERROR_STATE,
                    replace: true,
                })
                return
            }

            // AppInit's resetStore removed the token that OTP login needs.
            setVendorToken(fallbackVendorToken)
            setUpsellFlow(isUpsell)
            navigate(`/${CreditRoute.RequestOtp}`, { replace: true })
        },
    })
}

export const useResendOtp = () => {
    return useMutation({
        mutationFn: (phoneNumber: string) => checkoutService.resendOtp(phoneNumber),
    })
}

export const useUpsellCheckoutInit = () => {
    const navigate = useNavigate()
    const { resetStore, setVendorToken, setUpsellFlow } = useAppStore()

    return useMutation({
        mutationFn: (token: string) => checkoutService.upsellCheckoutInit(token),
        onSuccess: ({ token: vendorRequestToken }) => {
            resetStore()
            logoutUser()
            setVendorToken(vendorRequestToken)
            setUpsellFlow(true)
            navigate(`/${CreditRoute.RequestOtp}`, { replace: true })
        },
        onError: (error) => {
            const errorKey = isStandardErrorResponse(error) ? error.message : ''
            const variant =
                (UPSELL_RESPONSE_KEY_TO_VARIANT[errorKey] as UpsellBlockedVariant | undefined) ??
                UpsellBlockedVariant.OfferUnavailable
            navigate(`/${CreditRoute.UpsellBlocked}`, { state: { variant }, replace: true })
        },
    })
}

// Tokenless GPL marketing entry (/info) — init creates the checkout session server-side and
// returns the VendorRequest token; unknown error keys resolve to the GENERIC variant on the
// error screen, so no key mapping is needed here (unlike the upsell blocked-screen variants).
export const useGplMarketingCheckoutInit = () => {
    const navigate = useNavigate()
    const { resetStore, setVendorToken } = useAppStore()

    return useMutation({
        mutationFn: () => checkoutService.gplCheckoutInit(),
        onSuccess: ({ token: vendorRequestToken }) => {
            resetStore()
            logoutUser()
            setVendorToken(vendorRequestToken)
            navigate(`/${CreditRoute.RequestOtp}`, { replace: true })
        },
        onError: (error) => {
            const errorKey = isStandardErrorResponse(error) ? error.message : ''
            navigate(`/${CreditRoute.CreditError}`, { state: { error: errorKey }, replace: true })
        },
    })
}

export const useResendOtpOverInvoice = () => {
    return useMutation({
        mutationFn: (phoneNumber: string) => checkoutService.resendOtpOverVoice(phoneNumber),
    })
}

export const useGetLoanAmountLimits = (vendorId: string) => {
    return useQuery({
        queryKey: [CacheKey.LoanAmountLimits, vendorId],
        queryFn: () => checkoutService.getLoanAmountLimits({ vendorId }),
        enabled: !!vendorId,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useGetPurchaseLimit = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.PurchaseLimit, vendorToken],
        queryFn: async () => {
            const data = await checkoutService.getPurchaseLimit(vendorToken)

            void checkoutService.recordStep({
                token: vendorToken,
                checkoutStep: CheckoutStep.PURCHASE_LIMIT,
                checkoutStepViewType: CheckoutStepViewType.OPEN,
                data,
            })

            return data
        },
        enabled: !!vendorToken,
        staleTime: Infinity,
        retry: createStandardRetryHandler(),
    })
}

const HTTP_INTERNAL_SERVER_ERROR = 500
const PURCHASE_LIMIT_SERVER_ERROR_RETRIES = 1

// The purchase limit is offer-plan-data's hard dependency: no range means no screen to render,
// only the error page. So unlike the shared policy — which treats a 500 as terminal — this one
// endpoint gets a single extra attempt before the screen gives up.
const retryMplPurchaseLimit = (count: number, error: Error | StandardErrorResponse) => {
    if (isStandardErrorResponse(error) && error.status === HTTP_INTERNAL_SERVER_ERROR) {
        return count < PURCHASE_LIMIT_SERVER_ERROR_RETRIES
    }
    return createStandardRetryHandler()(count, error)
}

// SLO GPL loan-amount screen — fetch the MPL purchase limit (Bearer-auth + vendor token query param).
// Mirrors the legacy leanpay-client-new MPL flow (api/mpl/v1/purchase-limit).
export const useGetMplPurchaseLimit = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.MplPurchaseLimit, vendorToken],
        queryFn: async () => {
            const data = await checkoutService.getMplPurchaseLimit(vendorToken)

            void checkoutService.recordStep({
                token: vendorToken,
                checkoutStep: CheckoutStep.PURCHASE_LIMIT,
                checkoutStepViewType: CheckoutStepViewType.OPEN,
                data,
            })

            return data
        },
        enabled: !!getAuthToken() && !!vendorToken,
        staleTime: Infinity,
        retry: retryMplPurchaseLimit,
    })
}

export const useCalculatePaymentPlans = (
    params: Omit<CreditPaymentPlanRequest, 'token'> | null,
) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [
            CacheKey.PlanCalculation,
            vendorToken,
            params?.amount,
            params?.insuranceEnabled,
            params?.firstPaymentDate,
        ],
        queryFn: () =>
            checkoutService.calculatePaymentPlans({
                token: vendorToken,
                amount: params!.amount,
                insuranceEnabled: params!.insuranceEnabled,
                firstPaymentDate: params?.firstPaymentDate,
            }),
        enabled: !!vendorToken && params !== null,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60 * 5, // 5 minutes - data won't refetch if same params within this time
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useGetPaymentData = () => {
    return useMutation({
        mutationFn: (request: PaymentDataRequest) => checkoutService.getPaymentData(request),
    })
}

// SLO GPL terms screen — fetch MPL payment plans for the selected amount.
// firstPaymentDate is '' on first load and the selected cycle date (DDMMYYYY) on cycle change.
export const useGetMplPaymentData = (
    params: { amount: number; firstPaymentDate: string } | null,
) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.MplPaymentData, vendorToken, params?.amount, params?.firstPaymentDate],
        queryFn: () =>
            checkoutService.getMplPaymentData({
                token: vendorToken,
                amount: params!.amount,
                firstPaymentDate: params!.firstPaymentDate,
            }),
        enabled: !!vendorToken && params !== null,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

// SLO is FE-driven: plain mutation, navigation handled by the screen on success.
export const useFinancialProductInit = () => {
    return useMutation({
        mutationFn: (payload: FinancialProductV2Request) =>
            checkoutService.financialProductInit(payload),
    })
}

// SLO GPL loader (scoring-decision) — plain mutation, NOT useStateMachineMutation: each outcome
// routes to a distinct screen, so the loader branches on the result instead of always navigating
// to CreditError.
// financialProductInit is already called by the Terms screen — it must NOT be re-called here.
// selectedMplPlan is the plan chosen at the plan-data step; the assessment overlays the confirmed
// figures onto it, because only the chosen plan carries the maturity identifiers signing needs.
export const useRunInitialAssessment = () => {
    const { vendorToken, selectedMplPlan } = useAppStore()
    return useMutation({
        mutationFn: () => {
            // Only reachable by deep-linking past the plan-data step; the loader's catch turns
            // this into the generic error screen.
            if (!selectedMplPlan) {
                throw new Error(CreditFailureReason.Generic)
            }
            return checkoutService.runInitialAssessment(vendorToken, selectedMplPlan)
        },
    })
}

export const useGetContractData = () => {
    return useMutation({
        mutationFn: (payload: TransactionRequest) => checkoutService.getContractData(payload),
    })
}

export const useGetPepQuestionnaireROM = () => {
    return useQuery({
        queryKey: [CacheKey.PepQuestionnaire],
        queryFn: () => checkoutService.getPepQuestionnaireROM(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

// SLO GPL PEP questionnaire — legacy MPL endpoint (GET api/checkout/pep-questionnaire,
// user-auth context), single questionnaire instead of ROM's basic/additional pair.
export const useGetPepQuestionnaireSLO = () => {
    return useQuery({
        queryKey: [CacheKey.PepQuestionnaireSlo],
        queryFn: () => checkoutService.getPepQuestionnaire(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useDownloadInitialOffer = () => {
    return useMutation({
        mutationFn: (data: TransactionRequest) => downloadService.downloadInitialOffer(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })
}

export const useDownloadPreliminaryContract = () => {
    return useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPreliminaryContract(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })
}

export const useDownloadPrecontractualPeriodWaiver = () => {
    return useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPrecontractualPeriodWaiver(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })
}

export const useDownloadInsuranceDocument = () => {
    return useMutation({
        mutationFn: (token: string) => downloadService.downloadInsuranceDocument(token),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })
}

export const useGetPepStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.PepStatus, vendorToken],
        queryFn: () => checkoutService.getPepStatus(vendorToken),
        enabled: !!vendorToken,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useSignOrBuy = () => {
    return useMutation({
        mutationFn: ({
            payload,
            twoFaPin,
        }: {
            payload: ContractSignOrBuyRequest
            twoFaPin?: string
        }) => checkoutService.signOrBuyGpl(payload, twoFaPin),
    })
}

// SLO GPL PEP questionnaire submit — legacy POST api/checkout/questionnaire, sent before sign-or-buy.
export const useSubmitPepQuestionnaire = () => {
    return useMutation({
        mutationFn: (payload: QuestionnaireRequest) => checkoutService.submitQuestionnaire(payload),
    })
}

// SLO GPL post-sign final assessment poll (legacy getCheckoutStatus: throw while !isProcessed,
// retry({ delay: 5000, count: 6 })). The unprocessed sentinel is retried up to the legacy cap and
// then surfaces as isError (callers detect it via isMplPollPending); genuine transient failures
// (network blips, 5xx) get the standard retry handling instead of aborting straight to the error
// screen. The bounded poll deliberately uses retry instead of refetchInterval: exhaustion must
// surface as a state transition (isError), which an interval cannot guarantee when the final
// unprocessed response is structurally identical to the previous one.
const MPL_POLL_PENDING = 'MPL_CHECKOUT_STATUS_POLL_PENDING'
const MPL_POLL_MAX_RETRIES = 6
const MPL_POLL_DELAY_MS = 5000

export const isMplPollPending = (error: unknown): boolean =>
    error instanceof Error && error.message === MPL_POLL_PENDING

export const useMplCheckoutStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.MplCheckoutStatus, vendorToken],
        queryFn: async () => {
            const res = await checkoutService.getMplCheckoutStatus(vendorToken)
            if (!res.isProcessed) throw new Error(MPL_POLL_PENDING)
            return res
        },
        enabled: !!vendorToken && !!getAuthToken(),
        refetchOnWindowFocus: false,
        // No caching: a refresh on /credit-success must re-poll and re-derive the screen.
        gcTime: 0,
        retry: (count, error) =>
            isMplPollPending(error)
                ? count < MPL_POLL_MAX_RETRIES
                : createStandardRetryHandler()(count, error),
        retryDelay: MPL_POLL_DELAY_MS,
    })
}

export const useCreatePin = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (request: CreatePinRequest) => checkoutService.createPin(request),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] }),
    })
}

export const useRecreatePin = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (request: ReCreatePinRequest) => checkoutService.recreatePin(request),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] }),
    })
}

export const useGetCheckoutStateMachineStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.CheckoutStateMachineStatus, vendorToken],
        queryFn: () => checkoutService.getCheckoutStateMachineStatus(vendorToken),
        enabled: !!vendorToken,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useCancelTransaction = () => {
    return useMutation({
        mutationFn: (request: CancelTransactionRequest) =>
            checkoutService.cancelTransaction(request),
    })
}

export const useGetFinancialDataStatus = (enabled: boolean) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.FinancialDataStatus, vendorToken],
        queryFn: () => checkoutService.getFinancialDataStatus(vendorToken),
        enabled: enabled && !!vendorToken,
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
    })
}
